import React, {useCallback, useEffect, useMemo, useState} from "react";
import {getCroppedImg} from "@/helpers/getCroppedImage.ts";
import Cropper, {Area} from "react-easy-crop";
import {Button} from "@telegram-apps/telegram-ui";
import {useTranslation} from "react-i18next";
import {
  disableVerticalSwipes,
  enableVerticalSwipes,
  mainButton,
  secondaryButton
} from "@telegram-apps/sdk-react";
import {FullScreenLoader} from "@/components/loader/FullScreenLoader.tsx";
import {setOnClickMainButton, setOnClickSecondaryButton} from "@/helpers/tgButtons.ts";
import Compressor from "compressorjs";
import {haptic} from "@/helpers/haptic.ts";

const CROPPER_STYLE = {
  containerStyle: {
    backgroundColor: 'rgba(0, 0, 0, 0)',
  },
  cropAreaStyle: {
    border: '1px dashed #fff',
  },
};

function getSquareSize(offsetX: number, offsetY: number): number {
  const w = window.innerWidth - offsetX * 2;
  const h = window.innerHeight - offsetY * 2;
  return Math.floor(Math.min(w, h));
}

const CROPPER_AREA = {
  width: getSquareSize(30, 30),
  height: getSquareSize(30, 30),
}

// eslint-disable-next-line react/display-name
export const ImageCropDialog = React.memo((
  { file, onClose, onCrop }:
    { file: File, onClose: () => void, onCrop: (file: Blob) => void }
) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppingInProgress, setCroppingInProgress] = useState(false);
  const [compressingInProgress, setCompressingInProgress] = useState(true);
  const [compressedFile, setCompressedFile] = useState<File | null>(null);

  const { t } = useTranslation();

  console.debug(performance.now(), "ImageCropDialog rendered");
  console.debug(croppingInProgress, compressingInProgress, croppedAreaPixels, file, compressedFile);

  useEffect(() => {
    if (file) {
      console.debug(performance.now(), "COMPRESS IN PROGRESS", file.size);
      new Compressor(file, {
        quality: 0.8,
        maxHeight: 1000,
        maxWidth: 1000,
        success(result: File) {
          console.debug(performance.now(), "COMPRESS DONE", result.size);
          setCompressingInProgress(false);
          setCompressedFile(result);
        },
        error(err) {
          console.log(err.message);
        },
      });
    }
  }, [file]);

  const imageUrl = useMemo(() => {
    if (!compressedFile) {
      return;
    }
    return URL.createObjectURL(compressedFile)
  }, [compressedFile]);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) {
      return;
    }
    if (!imageUrl) {
      return;
    }
    setCroppingInProgress(true);
    const croppedImage = await getCroppedImg(imageUrl, croppedAreaPixels);
    setCroppingInProgress(false);

    if (!croppedImage) {
      haptic.notification('error');
      return;
    }

    onCrop(croppedImage);
    onClose();
  }, [imageUrl, onCrop, onClose, croppedAreaPixels]);

  useEffect(() => {
    console.debug(performance.now(), compressingInProgress, croppingInProgress);
    setOnClickMainButton(async () => {
      await handleSave();
    })
    mainButton.setParams({
      isVisible: true,
      isLoaderVisible: compressingInProgress || croppingInProgress,
      isEnabled: !compressingInProgress && !croppingInProgress,
      text: t('action.apply'),
    });

    setOnClickSecondaryButton(async () => {
      onClose();
    })
    if (secondaryButton.isSupported()) {
      secondaryButton.setParams({
        isVisible: true,
        isEnabled: !compressingInProgress && !croppingInProgress,
        text: t('action.cancel'),
      });
    }

    return () => {
    };
  }, [handleSave, compressingInProgress, croppingInProgress]);

  useEffect(() => {
    disableVerticalSwipes.ifAvailable()
    return () => {
      enableVerticalSwipes.ifAvailable()
      if (secondaryButton.isSupported()) {
        secondaryButton.setParams({
          isVisible: false,
        });
      }
      mainButton.setParams({
        isVisible: false,
      });
    }
  }, []);

  return (
    <div className={"absolute top-0 left-0 right-0 bottom-0 overlay"}>
      {/*{!compressedFile && <div className="absolute w-full h-full flex items-center justify-center">*/}
      {/*  Loading...*/}
      {/*</div>}*/}
      {!compressedFile && <FullScreenLoader/>}
      {compressedFile && <Cropper
        image={imageUrl}
        crop={crop}
        minZoom={0.5}
        maxZoom={5}
        zoom={zoom}
        aspect={1}
        restrictPosition={false}
        onCropChange={setCrop}
        onCropComplete={onCropComplete}
        onZoomChange={setZoom}
        style={CROPPER_STYLE}
        cropSize={CROPPER_AREA}
      />}
      {import.meta.env.MODE === 'development' && <div className={"p-5 flex justify-between gap-3"}>
        <Button stretched={true} onClick={handleSave} disabled={compressingInProgress || croppingInProgress}>{t('action.apply')}</Button>
        <Button stretched={true} onClick={() => {
          onClose();
        }} mode={"bezeled"}>{t('action.cancel')}</Button>
      </div>}
    </div>
  );
});
